import { PANEL_NAME_KEY } from '@allocab/allocab-commons-shared-types';
import { CarSolid } from '@allocab/allocab-icons';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { CheckBox } from '../../components/atomic/CheckBox';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { SecondaryButton } from '../../components/button/SecondaryButton';
import { FilterBadge } from '../../components/FilterBadge';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t } from '../../services/translation/translation';

export const PANEL_SEARCH_PARAMS_KEY = 'panelKey';
const panelNameKeys: PANEL_NAME_KEY[] = ['BERLINE', 'BUSINESS', 'VAO', 'VAN', 'MOTO', 'GREEN', 'TAXI', 'TAXI_CONV'];

type Props = {
  onChange: (_key: string, _value: string | undefined) => void;
};

export const TripPanelFilter = ({ onChange }: Props) => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const panelKey = searchParams.get(PANEL_SEARCH_PARAMS_KEY);
  const panelKeyFromParams = panelKey?.split(',') as PANEL_NAME_KEY[] | undefined;
  const [selectedPanels, setSelectedPanels] = useState<PANEL_NAME_KEY[]>(panelKeyFromParams ?? panelNameKeys);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleSave = () => {
    if (selectedPanels.length < panelNameKeys.length) {
      onChange(PANEL_SEARCH_PARAMS_KEY, selectedPanels.join(','));
    } else {
      onChange(PANEL_SEARCH_PARAMS_KEY, undefined);
    }
    setIsVisible(false);
  };

  const handleReset = () => {
    setSelectedPanels(panelNameKeys);
    onChange(PANEL_SEARCH_PARAMS_KEY, undefined);
    setIsVisible(false);
  };

  return (
    <div className="relative flex items-center justify-center">
      <FilterBadge onClick={() => setIsVisible(v => !v)} isActive={!!panelKeyFromParams}>
        <CarSolid width={12} height={12} className="fill-current" />
        <span>
          {panelKeyFromParams ? panelKeyFromParams.map(p => getPanelNameLabelByKey(p)).join(',') : t('common.panel')}
        </span>
      </FilterBadge>

      <div
        ref={ref}
        className={twJoin(
          'absolute top-10 z-10 w-48 bg-white rounded-lg shadow dark:bg-gray-700',
          !isVisible && 'hidden',
        )}
      >
        <ul className="p-3 space-y-1 text-sm text-gray-700 dark:text-gray-200">
          {panelNameKeys.map(panel => (
            <li key={panel}>
              <CheckBox
                id={`panel-filter-${panel}`}
                checked={selectedPanels.includes(panel)}
                onChange={value =>
                  value
                    ? setSelectedPanels(p => [...p, panel])
                    : setSelectedPanels(p => (p.length === 1 ? p : p.filter(s => s !== panel)))
                }
                label={getPanelNameLabelByKey(panel)}
              />
            </li>
          ))}
        </ul>
        <div className="space-y-2 px-2 mb-2">
          <PrimaryButton className="w-full" onClick={handleSave}>
            {t('common.apply')}
          </PrimaryButton>

          <SecondaryButton className="w-full" onClick={handleReset}>
            {t('common.reset')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};
